import * as React from 'react'
import { ApolloConsumer, Query, useMutation } from 'react-apollo'
import uuid from 'uuid/v1'

import { isFinalParentSignUpFormRequired } from 'main/components/common/AuthModal'
import Loader from 'main/components/common/Loader'
import { CredentialsFields } from 'main/gql/credentials'
import { LEAD_BOOKMARK_MUTATION, LeadBookmark, LeadBookmarkVariables } from 'main/gql/lead'
import { ADD_TO_FAVORITES_QUERY } from 'main/pages/School/CTA/Content/AddToFavorites/gql'
import { Modal } from 'main/ui'
import { UnreachableCaseError } from 'main/util/ts'

import {
  BookmarkAuthModal as BookmarkAuthModalData,
  BookmarkAuthModalVariables,
} from './__generated__/BookmarkAuthModal'
import Content from './Content'
import { BOOKMARK_AUTH_MODAL_QUERY } from './query'
import { track } from './track'
import { Step } from './types'
import { authModalInitialStep } from './util'

interface IProps {
  isBookmarked?: boolean
  schoolId: number
  onAuth?: (credentials: CredentialsFields) => void
  onRequestClose: () => void
}

export default function BookmarkAuthModal(props: IProps) {
  const { isBookmarked, schoolId, onAuth, onRequestClose } = props

  const [step, setStep] = React.useState<Step | null>(null)
  const [forgotPassword, setForgotPassword] = React.useState(false)

  const [sessionId] = React.useState(uuid())
  const trackData = { schoolId, sessionId }

  const [saveBookmark] = useMutation<LeadBookmark, LeadBookmarkVariables>(LEAD_BOOKMARK_MUTATION)

  React.useEffect(() => track('opened', trackData), [])

  return (
    <ApolloConsumer>
      {client => (
        <Query<BookmarkAuthModalData, BookmarkAuthModalVariables>
          query={BOOKMARK_AUTH_MODAL_QUERY}
          variables={{ schoolId }}
          onCompleted={async (data?: BookmarkAuthModalData) => {
            if (!!data && !step) {
              const maybeStep = authModalInitialStep(data.user) || Step.ParentFinal
              if (maybeStep === Step.ParentFinal && !isFinalParentSignUpFormRequired(data.user!)) {
                try {
                  await saveBookmark({
                    variables: { schoolId },
                    refetchQueries: [{ query: ADD_TO_FAVORITES_QUERY }],
                  })
                } catch {
                  // ignore and let the user click the button again
                } finally {
                  handleRequestClose()
                }
              } else {
                setStep(maybeStep)
              }
            }
          }}
        >
          {({ data, loading, error }) => {
            if (loading || error || !data) {
              return <LoadingModal onRequestClose={handleRequestClose} />
            }

            if (!step) {
              return <LoadingModal onRequestClose={onRequestClose} />
            }

            return (
              <Modal
                googleOptimize={googleOptimize()}
                title={forgotPassword ? 'Forgot password' : title(step, data)}
                onRequestClose={handleRequestClose}
              >
                <div>
                  <Content
                    client={client}
                    trackData={trackData}
                    isBookmarked={isBookmarked}
                    schoolId={schoolId}
                    step={step}
                    user={data.user}
                    onAuth={onAuth}
                    onChangeStep={setStep}
                    onForgotPassword={() => setForgotPassword(true)}
                    onRequestClose={handleRequestClose}
                  />
                </div>
              </Modal>
            )
          }}
        </Query>
      )}
    </ApolloConsumer>
  )

  function handleRequestClose() {
    track('closed', trackData)
    onRequestClose()
  }

  function googleOptimize() {
    return step && step === Step.ParentStart ? { title: 'bookmarkSignUpTitle' } : {}
  }
}

export function LoadingModal(props: { onRequestClose: () => void }) {
  const { onRequestClose } = props

  return (
    <Modal title="" onRequestClose={onRequestClose}>
      <Loader />
    </Modal>
  )
}

function title(step: Step, data: BookmarkAuthModalData) {
  const { school, user } = data

  switch (step) {
    case Step.Login:
      return 'Save to favorites'
    case Step.ParentStart:
      return 'Save to favorites'
    case Step.ParentIntermediate:
      return `Hi${user && user.firstName ? `, ${user.firstName}` : ''}`
    case Step.ParentFinal:
      if (!school) throw new Error('no school')
      return `Success! ${school.name} has been added to your favorites`
    default:
      throw new UnreachableCaseError(step)
  }
}
